import Order from "../models/Order";
import OrderContact from "../models/OrderContact";
import OrderLocation from "../models/OrderLocation";
import OrderNote from "../models/OrderNote";
import Document from "../models/Document";
import InsulationCut from "../models/InsulationCut";
import OrderItem from "../models/OrderItem";
import Note from "../models/Note";
import OrderStatus from "../models/OrderStatus";
import OrderStatusLog from "../models/OrderStatusLog";
import OrderCommit from "../models/OrderCommit";
import moment from "moment";

export default class OrderClass {

  constructor(payload, items, contacts, locations, notes, documents) {
    this.payload = payload;
    this.items = items;
    this.contacts = contacts;
    this.locations = locations;
    this.notes = notes;
    this.documents = documents;
    this.order = null;

    this.validation = OrderClass.validationDefaults();

    this.stepCallback = () => {};
    this.errorCallback = () => {};
  }

  setStepCallback(fn) {
    this.stepCallback = fn;
  }

  setErrorCallback(fn) {
    this.errorCallback = fn;
  }

  run() {
    this.stepCallback(1, 'Initializing Order', 9);
    this.commit(null, true);
    return Promise.all([
      this.createOrder()
    ]).then(() => {
      this.stepCallback(2, 'Creating Order Items', 9);
      return this.createOrderItems().then(() => {
        this.stepCallback(3, 'Setting On-site Contacts', 9);
        return this.createOrderContacts().then(() => {
          this.stepCallback(4, 'Setting Drop Locations', 9);
          return this.createOrderLocations().then(() => {
            this.stepCallback(5, 'Setting Order Notes', 9);
            return this.createOrderNotes().then(() => {
              this.stepCallback(6, 'Attaching Order Documents', 9);
              return this.createOrderDocuments().then(() => {
                this.stepCallback(7, 'Updating Order Status', 9);
                return this.createOrderStatusLog().then(() => {
                  this.stepCallback(8, 'Sending Notification', 9);
                  return this.exportOrder().then(() => {
                    this.stepCallback(9, 'Order Created Successfully', 9);
                    return this.order;
                  }, () => {
                    this.errorCallback('Could not send the status notification');
                  });
                }, () => {
                  this.errorCallback('Could not set pending status');
                });
              }, () => {
                this.errorCallback('Could not attach order documents');
              });
            }, () => {
              this.errorCallback('Could not set order notes');
            });
          }, () => {
            this.errorCallback('Could not set drop locations');
          });
        }, () => {
          this.errorCallback('Could not set on-site contacts');
        });
      }, () => {
        this.errorCallback('Could not create order items');
      });
    }, () => {
      this.errorCallback('Could not initialize order');
    });
  }

  getCommitData() {
    return {
      order: this.payload,
      order_items: this.items,
      order_contacts: this.contacts,
      order_locations: this.locations,
      order_notes: this.notes,
      order_documents: this.documents,
    };
  }

  commit(order, pre_flight) {
    pre_flight = !!pre_flight;
    let data = this.getCommitData();
    return OrderCommit.api().create({
      order_id: (order ? order.id : null),
      data,
      preflight_at: pre_flight ? moment().format('YYYY-MM-DD HH:mm:ss') : null
    }).then(() => {
      return order;
    });
  }

  static validationDefaults() {
    return {
      order: {
        po_number: null,
        estimated_date: null,
        transport_type: null,
      },
      drops: null,
      order_items: null,
    };
  }

  validate() {
    let is_valid = true;

    if(!this.payload.po_number || this.payload.po_number === '') {
      this.validation.order.po_number = 'Please provide a PO number or job name.';
      is_valid = false;
    } else {
      this.validation.order.po_number = true;
    }
    if(!this.payload.estimated_date || this.payload.estimated_date === '') {
      this.validation.order.estimated_date = 'Please provide a requested delivery date.';
      is_valid = false;
    } else {
      this.validation.order.estimated_date = true;
    }
    if(!this.payload.pickup && !this.payload.delivery) {
      this.validation.order.transport_type = 'Please provide how the order needs to be transported.';
      is_valid = false;
    } else {
      this.validation.order.transport_type = true;
    }

    let drop_index = [];
    if(!this.items || this.items.length === 0) {
      this.validation.order_items = 'No products were selected.';
      is_valid = false;
    } else if(this.items) {
      this.items.forEach((item) => {
        if(!item.product_id && !item.description) {
          this.validation.order_items = 'A product was not selected.';
          is_valid = false;
        } else if(!item.quantity) {
          this.validation.order_items = 'Quantity was not specified.';
          is_valid = false;
        } else if(!item.drop) {
          this.validation.order_items = 'Drop was not specified.';
          is_valid = false;
        }
        if(item.type && item.type.name === 'Insulation') {
          if([item.insulation_cut.width, item.insulation_cut.thickness, item.insulation_cut.length].filter(l=>!!l).length !== 3) {
            this.validation.order_items = 'You included insulation in your order, please specify the cut dimensions.';
            is_valid = false;
          }
        }
        if(item.drop && !drop_index.includes(item.drop)) {
          drop_index.push(item.drop);
        }
      });
      if(is_valid) {
        this.validation.order_items = true;
      }
    } else {
      this.validation.order_items = true;
    }

    if(this.payload.delivery) {

      if(!drop_index || !drop_index.length) {
        this.validation.drops = 'No drops were selected.';
        is_valid = false;
      } else {
        drop_index.forEach((drop_iter) => {
          let drop_contact = this.contacts[drop_iter];
          let drop_location = this.locations[drop_iter];
          if(drop_contact || drop_location) {
            if(!drop_location) {
              this.validation.drops = 'No drop locations were selected for drop '+drop_iter+'.';
              is_valid = false;
            }
          } else {
            this.validation.drops = 'No on-site contacts or drop locations were selected for drop '+drop_iter+'.';
            is_valid = false;
          }
        });
      }

    }

    if(is_valid) {
      this.validation.drops = true;
    }

    return is_valid;
  }

  createOrder() {
    return Order.api().create(this.payload).then((response) => {
      this.order = response.entities.orders[0];
    });
  }

  createOrderStatusLog() {
    return OrderStatus.api().fetch().then((response) => {
      let pending_status = null;
      response.entities.order_statuses.forEach((status) => {
        if(status && status.name == 'Pending') {
          pending_status = status;
        }
      });
      return OrderStatusLog.api().create({order_id: this.order.id, order_status_id: pending_status.id});
    });
  }

  createOrderItems() {
    if(!this.items) {
      return new Promise((resolve) => resolve);
    }
    let requests = [];
    this.items.forEach((item) => {
      delete item.type;
      delete item.category;
      item.order_id = this.order.id;
      let insulation_cut = item.insulation_cut;
      if(insulation_cut.thickness || insulation_cut.length || insulation_cut.width) {
        insulation_cut.product_id = item.product_id;
        requests.push(InsulationCut.api().create(insulation_cut).then((results) => {
          let cut = results.entities.insulation_cuts[0];
          item.insulation_cut_id = cut.id;
          return OrderItem.api().create(item);
        }));
      } else {
        requests.push(OrderItem.api().create(item));
      }
    });
    return Promise.all(requests);
  }

  createOrderContacts() {
    if(!this.contacts) {
      return new Promise((resolve) => resolve);
    }
    let requests = [];
    for(let drop in this.contacts) {
      let contact_id = this.contacts[drop];
      if(contact_id) {
        requests.push(OrderContact.api().create({
          order_id: this.order.id,
          contact_id: contact_id,
          drop: drop,
        }));
      }
    }
    return Promise.all(requests);
  }

  createOrderLocations() {
    if(!this.locations) {
      return new Promise((resolve) => resolve);
    }
    let requests = [];
    for(let drop in this.locations) {
      let location_id = this.locations[drop];
      if(location_id) {
        requests.push(OrderLocation.api().create({
          order_id: this.order.id,
          location_id: location_id,
          drop: drop,
        }));
      }
    }
    return Promise.all(requests);
  }

  createOrderNotes() {
    if(!this.notes) {
      return new Promise((resolve) => resolve);
    }
    let requests = [];
    this.notes.forEach((temp) => {
      requests.push(Note.api().create({note: temp.note}).then((results) => {
        let note = results.entities.notes[0];
        let payload = {drop: temp.drop, order_id: this.order.id, note_id: note.id};
        return OrderNote.api().create(payload);
      }));
    });
    return Promise.all(requests);
  }

  createOrderDocuments() {
    if(!this.documents) {
      return new Promise((resolve) => resolve);
    }
    let requests = [];
    this.documents.forEach((document) => {
      requests.push(Document.api().attachOrderDocument(this.order.id, document.id));
    });
    return Promise.all(requests);
  }

  exportOrder() {
    return Order.api().export(this.order.id);
  }

}